import { supabase } from '@/integrations/supabase/client';

interface TemplateResult {
  conteudo: string;
  ativo: boolean;
}

interface TemplateComVariacoesResult {
  mensagens: string[];
  ativo: boolean;
}

// Cache local para evitar queries repetidas na mesma sessão
let templateCache: Record<string, any> | null = null;
let cacheTimestamp = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutos

async function loadTemplates(): Promise<Record<string, any>> {
  const now = Date.now();
  if (templateCache && (now - cacheTimestamp) < CACHE_TTL) {
    return templateCache;
  }

  const { data, error } = await supabase
    .from('mensagens_template')
    .select('chave, conteudo, conteudo_variacao_2, conteudo_variacao_3, ativo')
    .order('ordem');

  if (error || !data) {
    console.warn('[mensagemTemplate] Erro ao carregar templates:', error);
    return templateCache || {};
  }

  const map: Record<string, any> = {};
  for (const t of data) {
    map[t.chave] = t;
  }

  templateCache = map;
  cacheTimestamp = now;
  return map;
}

/** Invalida o cache para forçar reload */
export function invalidateTemplateCache() {
  templateCache = null;
  cacheTimestamp = 0;
}

/**
 * Busca um template pelo chave. Se não existir ou estiver inativo, retorna o fallback.
 */
export async function getTemplate(chave: string, fallback: string): Promise<string> {
  try {
    const templates = await loadTemplates();
    const t = templates[chave];
    if (t && t.ativo) {
      return t.conteudo;
    }
    return fallback;
  } catch {
    return fallback;
  }
}

/**
 * Busca um template com variações (BKO usa 3 rotações).
 * Retorna array de mensagens e flag ativo.
 */
export async function getTemplateComVariacoes(
  chave: string,
  fallbacks: string[]
): Promise<TemplateComVariacoesResult> {
  try {
    const templates = await loadTemplates();
    const t = templates[chave];
    if (t && t.ativo) {
      const mensagens = [t.conteudo];
      if (t.conteudo_variacao_2) mensagens.push(t.conteudo_variacao_2);
      if (t.conteudo_variacao_3) mensagens.push(t.conteudo_variacao_3);
      return { mensagens, ativo: true };
    }
    return { mensagens: fallbacks, ativo: t ? t.ativo : true };
  } catch {
    return { mensagens: fallbacks, ativo: true };
  }
}

/**
 * Carrega todos os templates BKO (para useBKOAcao).
 * Retorna um mapa webhookKey -> { mensagens, mensagem_privada }
 */
export async function loadBKOTemplates(): Promise<Record<string, { mensagens: string[]; mensagem_privada: string }> | null> {
  try {
    const templates = await loadTemplates();
    
    // Mapa de chave do banco -> webhookKey do código
    const keyMap: Record<string, string> = {
      'bko.movel.criacao': 'CRIACAO_PEDIDO',
      'bko.movel.criacao_consolidado': 'CRIACAO_PEDIDO_CONSOLIDADO',
      'bko.movel.assinatura': 'ASSINATURA_REALIZADA',
      'bko.movel.assinatura_consolidado': 'ASSINATURA_REALIZADA_CONSOLIDADO',
      'bko.movel.inputado': 'PEDIDO_INPUTADO',
      'bko.movel.inputado_consolidado': 'PEDIDO_INPUTADO_CONSOLIDADO',
      'bko.movel.ativado': 'PEDIDO_ATIVADO',
      'bko.movel.ativado_consolidado': 'PEDIDO_ATIVADO_CONSOLIDADO',
      'bko.fibra.criacao': 'CRIACAO_PEDIDO_FIBRA',
      'bko.fibra.criacao_consolidado': 'CRIACAO_PEDIDO_FIBRA_CONSOLIDADO',
      'bko.fibra.assinatura': 'ASSINATURA_REALIZADA_FIBRA',
      'bko.fibra.assinatura_consolidado': 'ASSINATURA_REALIZADA_FIBRA_CONSOLIDADO',
      'bko.fibra.inputado': 'PEDIDO_INPUTADO_FIBRA',
      'bko.fibra.inputado_consolidado': 'PEDIDO_INPUTADO_FIBRA_CONSOLIDADO',
      'bko.fibra.ativado': 'PEDIDO_ATIVADO_FIBRA',
      'bko.fibra.ativado_consolidado': 'PEDIDO_ATIVADO_FIBRA_CONSOLIDADO',
    };

    const result: Record<string, { mensagens: string[]; mensagem_privada: string }> = {};
    let hasAny = false;

    for (const [dbKey, webhookKey] of Object.entries(keyMap)) {
      const t = templates[dbKey];
      if (t && t.ativo) {
        hasAny = true;
        const mensagens = [t.conteudo];
        if (t.conteudo_variacao_2) mensagens.push(t.conteudo_variacao_2);
        if (t.conteudo_variacao_3) mensagens.push(t.conteudo_variacao_3);
        result[webhookKey] = { mensagens, mensagem_privada: '' }; // mensagem_privada mantida do hardcoded
      }
    }

    return hasAny ? result : null;
  } catch {
    return null;
  }
}
